import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import { createApiClient } from "../api/client";
import type { McpTenantDetail } from "../api/mcp";
import type { AppConfig } from "../config";

export type TenantDetailsByRef = Record<string, McpTenantDetail>;

/**
 * `GET /admin/tenant/{ref}` for a small, specific set of references, fanned
 * out in parallel.
 *
 * Exists because `GET /admin/tenants` only carries a per-tenant
 * `blocked_users_count` now, not the names — those moved onto tenant detail
 * (8127fde). A fleet-wide blocklist route does not exist, so this is the
 * honest way to recover names and `blocked_reason`/`blocked_by`/
 * `blocked_since` for the handful of merchants that actually have a blocked
 * user, without reading the whole fleet's detail.
 *
 * One ref failing (404, 403, a dead gateway) does not blank the others —
 * each result is independent, keyed by reference.
 */
export function useTenantDetails(
  config: AppConfig,
  refs: string[],
): { byRef: TenantDetailsByRef; loading: boolean } {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const [byRef, setByRef] = useState<TenantDetailsByRef>({});
  const [loading, setLoading] = useState(false);
  // Refs are an array literal every render — key on its sorted content so the
  // effect does not re-fan-out on every parent re-render.
  const key = [...refs].sort().join(",");

  useEffect(() => {
    if (refs.length === 0) {
      setByRef({});
      return;
    }
    let active = true;
    setLoading(true);
    const api = createApiClient({ apiUrl: config.apiUrl, getIdToken: () => token });

    void Promise.all(
      refs.map(async (ref) => {
        try {
          const detail = await api.request<McpTenantDetail>(
            `/mcp/admin/tenant/${encodeURIComponent(ref)}`,
          );
          return [ref, detail] as const;
        } catch {
          // One merchant's detail failing to load is not the page failing —
          // the table falls back to the count it already had for that row.
          return null;
        }
      }),
    ).then((results) => {
      if (!active) return;
      const next: TenantDetailsByRef = {};
      for (const r of results) {
        if (r) next[r[0]] = r[1];
      }
      setByRef(next);
      setLoading(false);
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.apiUrl, token, key]);

  return { byRef, loading };
}
